import { nanoid } from 'nanoid';

import {
  type MultipleSet,
  type OxSet,
  type QuizSetData,
  type SubjectiveSet,
} from '@/api';

import { getBlankQuizItem } from '../form/schema/getBlankQuizItem';

/**
 * QuizSetData를 BuildSchema에 맞도록 형태를 변환합니다.
 * 서버와 논의하여, QuizSetDataV2로 migration을 진행하기를 권장합니다.
 * @param quizzes - Quiz set data
 * @returns Build schema input type
 */
export const adaptQuizSetDataToBuildSchema = (quizzes: QuizSetData[]) => {
  const newQuizList = quizzes.map(quiz => {
    const baseQuiz = {
      ...getBlankQuizItem(),
      //서버에서는 nullable 데이터가 null로 오는데, 클라에서는 optional의 경우 undefined여야 schema를 통과합니다.
      // 따라서 nullish 병합 연산자를 사용하여 undefined로 변환합니다.
      questionImageUrl: quiz.imageUrl ?? undefined,
      hint:
        quiz.hint && quiz.hint.value
          ? {
              type: quiz.hint.type as 'text' | 'image' | 'youtube',
              value: quiz.hint.value,
            }
          : undefined,
      id: quiz.id,
    };

    switch (quiz.type) {
      case 'subjective':
        return {
          ...baseQuiz,
          question: quiz.subjectiveData?.question ?? '',
          questionRichText: quiz.subjectiveData?.questionRichText ?? undefined,
          type: 'subjective' as const,
          subjectiveData: adaptSubjectiveData(
            quiz.subjectiveData as SubjectiveSet,
          ),
        };
      case 'multiple':
        return {
          ...baseQuiz,
          question: quiz.multipleData?.question ?? '',
          questionRichText: quiz.multipleData?.questionRichText ?? undefined,
          type: 'multiple' as const,
          multipleData: adaptMultipleData(quiz.multipleData as MultipleSet),
        };
      case 'ox':
        return {
          ...baseQuiz,
          question: quiz.oxData?.question ?? '',
          questionRichText: quiz.oxData?.questionRichText ?? undefined,
          oxData: adaptOXData(quiz.oxData as OxSet),
          type: 'ox' as const,
        };
      case 'essay':
        return {
          ...baseQuiz,
          question: quiz.essayData?.question ?? '',
          questionRichText: quiz.essayData?.questionRichText ?? undefined,
          type: 'essay' as const,
        };
      default:
        throw new Error('Invalid quiz type');
    }
  });

  return newQuizList;
};

const adaptMultipleData = (multipleData: MultipleSet) => {
  const choiceList = multipleData.choiceListV2.map((choice, index) => ({
    ...choice,
    key: nanoid(8),
    richText: choice.richText ?? undefined,
    imageUrl: choice.imageUrl ?? undefined,
    isAnswer: multipleData.answerList.includes(String(index)),
  }));

  return {
    choiceList,
  };
};

const adaptSubjectiveData = (subjectiveData: SubjectiveSet) => {
  return {
    answerList: subjectiveData.answerList.map(answer => ({
      key: nanoid(8),
      text: answer,
    })),
  };
};

const adaptOXData = (oxData: OxSet) => {
  return {
    answer: oxData.answer as 'o' | 'x',
  };
};
